import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { Observable, Subject, of } from "rxjs";
import { map } from "rxjs/operators";
import { HttpService } from "services/http.service";
import {
	WorkforcePlanSummaryModel,
	WorkforcePlanTeamModel,
} from "services/models/workforceplan";
import { WorkforcePlanSolicitationModel } from "services/models/workforceplan/workforceplan-solicitation.model";
import { ServiceBase } from "services/servicebase";
import _ from 'lodash'
import { WorkforcePlanSolicitationDetailsModel } from "services/models/workforceplan/workforceplan-solicitation-details.model";
import { WorkforcePlanOrganicEvolutionModel, WorkforcePlanReportModel } from "services/models/workforceplan/workforceplan-report";
import {
	WorkforcePlanReportEmployeeApiItem,
	WorkforcePlanReportEmployeesApiPage,
	WorkforcePlanReportEmployeeModel,
	WorkforcePlanReportEmployeesPageModel,
	WorkforcePlanReportEmployeesQuery,
} from "services/models/workforceplan/workforceplan-report-employees.model";
import { WorkforceplanEmployeeStatus } from "services/enums/WorkforceplanEmployeeStatus.enum";
import { WorkforceplanMovementType } from "services/enums/WorkforceplanMovementType.enum";
import { MovementRequestPermissionModel } from "services/models/workforceplan/movement-request-permission.model";
import { WorkforcePlanModel } from "services/models/workforceplan/workforceplan-main.model";
import { Response } from "services/models/response";
import type { OptionModel } from "services/models/model";
import { WorkforcePlanCalendarModel } from "services/models/workforceplan/workforceplan-calendar.model";
import { QueryParams } from "services/models/odata/queryParams";
import { Filter } from "services/models/odata/filter";
import { WorkforceplanStatusType } from "services/enums/WorkforceplanStatusType.enum";
import { EmployeeDisciplinaryMeasureModel } from "services/models/corporate/employee-disciplinary-measure.model";
import { WorkforcePlanWorkScaleGroupModel } from "services/models/workforceplan/workforceplan-workscale-group.model";
import { WorkforcePlanAmountModel } from "services/models/workforceplan/workforceplan-amount.model";
import {
	WorkforcePlanHeadcountPreview,
	WorkforcePlanHeadcountPreviewExclusion,
} from "services/models/workforceplan/workforceplan-headcount-preview.model";

type StateScreenActionsResponse = {
	shouldRefetchCountSolicition?: boolean;
};

type StateWorkforceplanDataResponse = {
	data?: WorkforcePlanModel;
	form?: any;
};

@Injectable()
export class WorkforceplanService extends ServiceBase<WorkforcePlanModel> {
	private stateActionsSubject = new Subject<StateScreenActionsResponse>();
	public stateScreenActions$ = this.stateActionsSubject.asObservable();

	private stateWorkforceplanSubject = new Subject<StateWorkforceplanDataResponse>();
	public stateWorkforceplanData$ = this.stateWorkforceplanSubject.asObservable();

	constructor(http: HttpService) {
		super("WorkForcePlan", http, environment.apiUrl);
	}

	createEntity(input: any): WorkforcePlanModel {
		return WorkforcePlanModel.create(input);
	}

	emitEventUpdateStateScreenActions(data: StateScreenActionsResponse) {
		this.stateActionsSubject.next(data);
	}

	emitEventUpdateStateWorkforceplanData(data: StateWorkforceplanDataResponse) {
		this.stateWorkforceplanSubject.next(data);
	}

	override get(id: string, disableLoading?: boolean): Observable<Response<WorkforcePlanModel>> {
		return this.http
			.get(`${this.endpoint}/${id}`, undefined, this.baseUrl, undefined, disableLoading)
			.pipe(
				map((res) => {
					const response = new Response<WorkforcePlanModel>();
					response.data = this.parseWorkforcePlanData(res.body || {});
					return response;
				})
			);
	}

	getWorkforcePlan(companyId: string, costCenterId: string, disableLoading = false): Observable<WorkforcePlanModel[]> {
		return this.http.get(`${this.endpoint}/get-by-company-and-costcenter?companyId=${companyId}&costCenterId=${costCenterId}`, null, null, null, disableLoading).pipe(
			map((res) => {
				return res.body.map((wfp: WorkforcePlanModel) => this.parseWorkforcePlanData(wfp));
			})
		);
	}

	getPreviewFromCurrentHeadcount(
		companyId: string,
		costCenterId: string,
		byJobTitle: boolean
	): Observable<WorkforcePlanHeadcountPreview> {
		return this.http
			.get(
				`${this.endpoint}/preview-from-current-headcount?companyId=${companyId}&costCenterId=${costCenterId}&byJobTitle=${byJobTitle}`,
				null,
				null,
				null,
				false
			)
			.pipe(map((res) => this.parseHeadcountPreview(res.body)));
	}

	createWorkforcePlan(payload: WorkforcePlanModel): Observable<string | null> {
		return this.http.post(`/${this.endpoint}`, payload, undefined, this.baseUrl).pipe(
			map((res) => this.resolveCreatedWorkforcePlanId(res?.body))
		);
	}

	getWorkforcePlanByVersionId(versionId: string, disableLoading = false): Observable<WorkforcePlanModel> {
		return this.http.get(`${this.endpoint}/version/${versionId}`, null, null, null, disableLoading).pipe(
			map((res) => this.parseWorkforcePlanData(res.body))
		);
	}

	getPermissionsByMovementType(
		movementType: WorkforceplanMovementType,
		companyId?: string
	): Observable<MovementRequestPermissionModel> {
		return this.http
			.get(
				`${this.endpoint}/permission-by-movement-type/${movementType.id}`,
				null,
				null,
				companyId ? { companyId } : null,
				true
			)
			.pipe(map((res) => MovementRequestPermissionModel.create(res.body)));
	}

	loadWorkScales(companyId: string): Observable<any[]> {
		return this.http.get(`TimeShift/${companyId}/work-scale-groups-by-company`).pipe(
			map((res) => {
				return res.body.map((item: any) => ({ id: item, title: item }));
			})
		);
	}

	getWorkforcePlanSummary(id: string): Observable<WorkforcePlanSummaryModel> {
		return this.http.get(`${this.endpoint}/${id}/summary`, null, null, null, true).pipe(
			map((res) => {
				return WorkforcePlanSummaryModel.create(res.body);
			})
		);
	}

	getWorkforcePlanTeam(
		id: string,
		status: number = WorkforceplanEmployeeStatus.None.id,
		apprenticesOnly = false
	): Observable<WorkforcePlanTeamModel[]> {
		const queryParts = [`status=${status}`];
		if (apprenticesOnly) {
			queryParts.push("apprenticesOnly=true");
		}

		return this.http.get(`${this.endpoint}/${id}/team?${queryParts.join("&")}`, null, null, null, true).pipe(
			map((res) => {
				const data = res.body.map((item: any) => WorkforcePlanTeamModel.create(item));
				return data
			})
		);
	}

	getCountWorkforcePlanPendingSolicitation(wfpId: string): Observable<any> {
		return this.http.get(`${this.endpoint}/${wfpId}/movement-request/pending`, null, null, null, true).pipe(
			map((res) => {
				return res.body
			})
		);
	}

	getWorkforcePlanSolicitation(id: string): Observable<WorkforcePlanSolicitationModel[]> {
		return this.http.get(`${this.endpoint}/${id}/movement-request/odata?$orderBy=CreationDate desc`, null, null, null, true).pipe(
			map((res) => {
				return res.body.map((item: any) => {
					const created = WorkforcePlanSolicitationModel.create(item);

					const rejectedIndex = created.steps.findIndex(f => f.status === 'REJECTED');
					if (rejectedIndex >= 0)
						created.steps = created.steps.slice(0, rejectedIndex + 1)

					return created;
				})
			})
		);
	}

	getWorkforcePlanSolicitationById(wfpId: string, solicitationId: string): Observable<WorkforcePlanSolicitationModel[]> {
		return this.http.get(`${this.endpoint}/${wfpId}/movement-request/odata?$filter=id eq ${solicitationId}&$orderBy=CreationDate desc`, null, null, null, true).pipe(
			map((res) => {
				return res.body.map((item: any) => {
					const created = WorkforcePlanSolicitationModel.create(item);

					const rejectedIndex = created.steps.findIndex(f => f.status === 'REJECTED');
					if (rejectedIndex >= 0)
						created.steps = created.steps.slice(0, rejectedIndex + 1)

					return created;
				})
			})
		);
	}

	getWorkforcePlanSolicitationPermission(approvalScopeItemId: string): Observable<{ canExecute: boolean; canUpdate: boolean; }> {
		return this.http.get(`${this.endpoint}/movement-request/${approvalScopeItemId}/permission`, null, null, null, true).pipe(
			map((res) => {
				return res.body
			})
		);
	}

	postWorkforcePlanSolicitationUpdateStatus(movementRequestId: string, data: any) {
		return this.http.post(`${this.endpoint}/movement-request/${movementRequestId}/status`, data, null, null, true, true)
	}

	getWorkforcePlanSolicitationDetails(movementRequestId: string, approvalScopeItemId: string): Observable<WorkforcePlanSolicitationDetailsModel> {
		return this.http.get(`${this.endpoint}/movement-request/${movementRequestId}/details/${approvalScopeItemId}`, null, null, null, true).pipe(
			map((res) => WorkforcePlanSolicitationDetailsModel.create(res.body))
		);
	}

	private _buildReportQuery(
		workforcePlanId: string,
		costCenterId: string,
		companyId: string,
		chartFilters: { field: string; value: string }[],
		includeDetails = false
	): string {
		let query = '';

		if (workforcePlanId) {
			query += `?wfpId=${workforcePlanId}`;
		}

		if (costCenterId) {
			query += query ? `&costCenterId=${costCenterId}` : `?costCenterId=${costCenterId}`;
		}

		if (companyId) {
			query += query ? `&companyId=${companyId}` : `?companyId=${companyId}`;
		}

		chartFilters.forEach(filter => {
			const encodedValue = encodeURIComponent(filter.value);
			query += query ? `&${filter.field}=${encodedValue}` : `?${filter.field}=${encodedValue}`;
		});

		if (includeDetails) {
			query += query ? `&includeDetails=true` : `?includeDetails=true`;
		}

		return query;
	}

	getWorkforcePlanReports(
		workforcePlanId: string,
		costCenterId: string,
		companyId: string,
		chartFilters: { field: string, value: string }[],
		includeDetails = false
	): Observable<WorkforcePlanReportModel> {
		const query = this._buildReportQuery(
			workforcePlanId,
			costCenterId,
			companyId,
			chartFilters,
			includeDetails
		);

		return this.http.get(`${this.endpoint}/report${query}`, null, null, null, true).pipe(
			map((res) => WorkforcePlanReportModel.create(res.body))
		);
	}

	getWorkforcePlanReportEmployees(
		params: WorkforcePlanReportEmployeesQuery
	): Observable<WorkforcePlanReportEmployeesPageModel> {
		const queryParts: string[] = [];
		const append = (key: string, value: string | number | boolean | undefined | null) => {
			if (value === undefined || value === null || value === "") {
				return;
			}
			queryParts.push(`${key}=${encodeURIComponent(String(value))}`);
		};
		const appendAll = (key: string, values?: string[]) => {
			(values ?? []).filter(Boolean).forEach((value) => append(key, value));
		};

		append("wfpId", params.workforcePlanId);
		append("companyId", params.companyId);
		append("costCenterId", params.costCenterId);
		appendAll("companyIds", params.companyIds);
		appendAll("costCenterIds", params.costCenterIds);
		appendAll("workScaleGroupIds", params.workScaleGroupIds);
		appendAll("jobTitleGroupIds", params.jobTitleGroupIds);
		appendAll("jobTitleIds", params.jobTitleIds);
		appendAll("areas", params.areas);
		append("status", params.status ?? 0);
		append("situation", params.situation);
		append("apprenticesOnly", params.apprenticesOnly ? true : undefined);
		append("search", params.search);
		append("skip", params.skip ?? 0);
		append("top", params.top ?? 30);
		append("orderBy", params.orderBy);
		append("orderByOrder", params.orderByOrder);

		const query = queryParts.length ? `?${queryParts.join("&")}` : "";

		return this.http.get(`${this.endpoint}/report/employees${query}`, null, null, null, true).pipe(
			map((res) => {
				const body = (res.body || {}) as WorkforcePlanReportEmployeesApiPage;
				const defaultAvatar = "/assets/images/logo/avatar-profile.png";
				return {
					count: body.count ?? 0,
					items: (body.items ?? []).map((item) =>
						this.mapReportEmployee(item, defaultAvatar)
					),
				};
			})
		);
	}

	private mapReportEmployee(
		item: WorkforcePlanReportEmployeeApiItem,
		defaultAvatar: string
	): WorkforcePlanReportEmployeeModel {
		const workScaleGroup = (item.workScaleGroup ?? "").trim();
		const shiftSchedule = (item.shiftSchedule ?? "").trim();
		const profileImagePath = (item.profileImagePath ?? "").trim();
		let shiftLabel = "---";
		if (workScaleGroup && shiftSchedule) {
			shiftLabel = `${workScaleGroup} - ${shiftSchedule}`;
		} else if (workScaleGroup || shiftSchedule) {
			shiftLabel = workScaleGroup || shiftSchedule;
		}

		return {
			employeeId: item.employeeId,
			registration: item.registration ?? "",
			name: item.name ?? "",
			companyId: item.companyId ?? "",
			company: item.company ?? "",
			area: item.area ?? "",
			costCenterId: item.costCenterId ?? "",
			costCenter: item.costCenter ?? "",
			workScaleGroupId: item.workScaleGroupId ?? "",
			workScaleGroup,
			shiftSchedule,
			shiftLabel,
			jobTitleGroupId: item.jobTitleGroupId ?? "",
			jobTitleGroupName: item.jobTitleGroupName ?? "",
			jobTitleId: item.jobTitleId ?? "",
			jobTitleName: item.jobTitleName ?? "",
			status: item.status ?? "",
			isApprentice: !!item.isApprentice,
			movementRequestId: item.movementRequestId,
			profileImagePath,
			avatarUrl: profileImagePath || defaultAvatar,
		};
	}

	getWorkforcePlanOrganicEvolution(workforcePlanId: string, costCenterId: string, companyId: string, chartFilters: { field: string, value: string }[]): Observable<WorkforcePlanOrganicEvolutionModel> {
		const query = this._buildReportQuery(workforcePlanId, costCenterId, companyId, chartFilters);

		return this.http.get(`${this.endpoint}/report/organic-evolution${query}`, null, null, null, true).pipe(
			map((res) => WorkforcePlanOrganicEvolutionModel.create(res.body))
		);
	}

	getWorkforcePlanCalendar(workforcePlanId: string, start: string, end: string): Observable<WorkforcePlanCalendarModel[]> {
		let query = `?startDate=${start}&endDate=${end}`;

		return this.http.get(`${this.endpoint}/${workforcePlanId}/calendar${query}`, null, null, null, true).pipe(
			map((res) => res.body.map((item: any) => WorkforcePlanCalendarModel.create(item)))
		);
	}

	getWorkforcePlanByResourceUrl(url: string): Observable<any> {
		return this.http.get(url, null, null, null, true).pipe(
			map((res) => {
				return res.body
			})
		);
	}

	removeDraft(id: string): Observable<Response<any>> {
		return this.http.remove(`${this.endpoint}/remove-draft/${id}`).pipe(
			map((res) => {
				const response = new Response<any>();
				response.data = res;
				return response;
			})
		);
	}

	listJobTitleGroupsFromWorkforcePlan(companyId: string, costCenterId: string): Observable<Response<OptionModel[]>> {
		return this.http.get(`${this.endpoint}/get-jobtitle-groups-by-company-and-costcenter?costCenterId=${costCenterId}&companyId=${companyId}`, null, null, null, true).pipe(
			map((res) => {
				const response = new Response<OptionModel[]>();

				response.data = res.body.map((item: any) => ({ value: item.id, label: item.description } as OptionModel));

				response.count = res.headers.get("x-count");

				return response;
			})
		);
	}

	listEffectiveCostCentersByCompany(companyId: string): Observable<Response<OptionModel[]>> {
		return this.http.get(`${this.endpoint}/effectives/cost-centers-options-by-company?companyId=${companyId}`).pipe(
			map((res) => {
				const response = new Response<OptionModel[]>();

				response.data = res.body.map((item: any) => ({ value: item.id, label: item.description } as OptionModel));

				response.count = res.headers.get("x-count");

				return response;
			})
		);
	}

	listDisciplinaryMeasuresByEmployee(employeeId: string): Observable<EmployeeDisciplinaryMeasureModel[]> {
		if (!employeeId) {
			return of([]);
		}

		return this.http
			.get(`${this.endpoint}/disciplinary-measure/list-by-employee/${employeeId}`, null, null, null, true)
			.pipe(
				map((res: any) => {
					const body = res?.body ?? res;
					const items = Array.isArray(body) ? body : [];
					return items.map((item) => EmployeeDisciplinaryMeasureModel.create(item));
				})
			);
	}

	private resolveCreatedWorkforcePlanId(body: unknown): string | null {
		if (!body) {
			return null;
		}

		if (typeof body === "string") {
			return body || null;
		}

		const entity = body as { id?: string; data?: { id?: string } };
		return entity.id || entity.data?.id || null;
	}

	private parseWorkforcePlanData(workfocePlan: WorkforcePlanModel) {
		if (!workfocePlan) return null;
		workfocePlan.workScaleGroups = this.parsePreviewWorkScaleGroups(workfocePlan.workScaleGroups || []);
		return workfocePlan;
	}

	private parseHeadcountPreview(body: unknown): WorkforcePlanHeadcountPreview {
		if (Array.isArray(body)) {
			return {
				groups: this.parsePreviewWorkScaleGroups(body),
				exclusions: [],
			};
		}

		const payload = (body || {}) as {
			groups?: WorkforcePlanWorkScaleGroupModel[];
			exclusions?: WorkforcePlanHeadcountPreviewExclusion[];
			data?: {
				groups?: WorkforcePlanWorkScaleGroupModel[];
				exclusions?: WorkforcePlanHeadcountPreviewExclusion[];
			};
		};
		const envelope = payload.groups !== undefined || payload.exclusions !== undefined
			? payload
			: (payload.data || {});

		return {
			groups: this.parsePreviewWorkScaleGroups(envelope.groups || []),
			exclusions: (envelope.exclusions || []).map(item => this.parseHeadcountPreviewExclusion(item)),
		};
	}

	private parseHeadcountPreviewExclusion(
		item: WorkforcePlanHeadcountPreviewExclusion
	): WorkforcePlanHeadcountPreviewExclusion {
		return {
			employeeId: item?.employeeId || "",
			name: item?.name || "",
			registration: item?.registration || "",
			reasons: Array.isArray(item?.reasons) ? item.reasons : [],
		};
	}

	private parsePreviewWorkScaleGroups(groups: WorkforcePlanWorkScaleGroupModel[]): WorkforcePlanWorkScaleGroupModel[] {
		const workScaleGroups = [...groups].sort((a, b) =>
			(a.workScaleGroup || "").localeCompare(b.workScaleGroup || "")
		);

		workScaleGroups.forEach(item => {
			item.amounts = (item.amounts || []).map((ia: WorkforcePlanAmountModel) => {
				if (!ia.jobTitleName) {
					ia.jobTitleId = undefined;
					ia.jobTitleName = undefined;
				}
				return ia;
			});

			item.amounts = item.amounts.sort((a, b) =>
				(a.jobTitleGroupName || "").localeCompare(b.jobTitleGroupName || "")
			);
		});

		return workScaleGroups;
	}

	getActiveWorkforcePlansByCompany(companyId: string): Observable<Response<WorkforcePlanModel[]>> {
		const filter = [
			`companyId eq ${companyId}`,
			`status ne '${WorkforceplanStatusType.Discontinued.value}'`
		]

		const params = {
			select: ['costCenterId'],
			filter: Filter.multEquals(filter),
		} as QueryParams;

		const query = this.createBuildQuery(params);
		return this.http.get(`${this.endpoint}/odata${query}`).pipe(
			map((res) => {
				const response = new Response<WorkforcePlanModel[]>();

				response.data = res.body;

				response.count = res.headers.get("x-count");

				return response;
			})
		);
	}
}
